import {
  Plus,
  MoreHorizontal,
  Play,
  Link,
  Upload,
  Smartphone,
  RectangleHorizontal,
  Clock,
  Film,
  Wand2,
  Mic,
  ChevronRight,
  Check,
  X,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { generateProjectId } from '../../utils/projectId'
import { RecipeItem } from './mockData'

interface PromptInputProps {
  selectedRecipe?: RecipeItem | null
  onClearRecipe?: () => void
}

export default function PromptInput({ selectedRecipe, onClearRecipe }: PromptInputProps) {
  const navigate = useNavigate()
  const [placeholderText, setPlaceholderText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [loopNum, setLoopNum] = useState(0)
  const [typingSpeed, setTypingSpeed] = useState(50)
  const [inputValue, setInputValue] = useState('')

  const [activeDropdown, setActiveDropdown] = useState<'add' | 'ratio' | 'more' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleGenerate = () => {
    const projectId = generateProjectId()
    navigate(`/project/${projectId}`)
  }

  useEffect(() => {
    if (selectedRecipe) {
      setInputValue('')
    }
  }, [selectedRecipe])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (selectedRecipe) return

    const phrases = [
      'Ask Sequence to visualize a sci-fi narrative inspired by The Wandering Earth',
      'Ask Sequence to create a high-energy commercial for a new energy drink.',
      'Ask Sequence to produce a documentary-style explainer on black holes.',
    ]

    const handleType = () => {
      const i = loopNum % phrases.length
      const fullText = phrases[i] ?? ''

      setPlaceholderText(
        isDeleting
          ? fullText.substring(0, placeholderText.length - 1)
          : fullText.substring(0, placeholderText.length + 1)
      )

      if (!isDeleting && placeholderText === fullText) {
        setTimeout(() => setIsDeleting(true), 2000)
        setTypingSpeed(50)
      } else if (isDeleting && placeholderText === '') {
        setIsDeleting(false)
        setLoopNum(loopNum + 1)
        setTypingSpeed(50)
      } else {
        setTypingSpeed(isDeleting ? 30 : 50)
      }
    }

    const timer = setTimeout(handleType, typingSpeed)

    return () => clearTimeout(timer)
  }, [placeholderText, isDeleting, loopNum, typingSpeed, selectedRecipe])

  const toggleDropdown = (name: 'add' | 'ratio' | 'more') => {
    setActiveDropdown(activeDropdown === name ? null : name)
  }

  return (
    <div
      className="relative rounded-[2rem] bg-white p-4 focus-within:outline-none focus-within:ring-0"
      ref={dropdownRef}
    >
      <div className="relative min-h-24 w-full">
        {selectedRecipe ? (
          <div className="p-4 text-left">
            <div className="leading-normal text-lg flex flex-wrap items-center gap-x-2 gap-y-2">
              <div
                contentEditable="false"
                className="recipe-item relative inline-flex h-[26px] items-center gap-1.5 rounded-lg border border-white/20 bg-gray-900 px-2 py-1 text-sm font-medium text-white select-none shadow-sm overflow-hidden shrink-0"
              >
                {/* Background Image with Overlay */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${selectedRecipe.posterUrl})` }}
                />
                <div className="absolute inset-0 bg-black/40" />
                
                <span className="relative z-10 leading-none">{selectedRecipe.title}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onClearRecipe?.()
                  }}
                  className="relative z-10 ml-1 flex h-3.5 w-3.5 items-center justify-center rounded-full text-white/70 hover:bg-white/20 hover:text-white"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
              <span key={selectedRecipe.id} className="animate-blur-fade-in text-gray-900">
                Create a short film in the style of [a director&apos;s name] about [your theme of film]
              </span>
            </div>
          </div>
        ) : (
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholderText}
            className="no-focus-ring h-24 w-full resize-none rounded-xl border-none bg-transparent p-4 text-lg text-gray-600 outline-none placeholder:text-gray-300 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none"
          />
        )}
      </div>

      <div className="flex items-center justify-between px-2 pb-2">
        <div className="relative flex items-center gap-4">
          {/* Add Button */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('add')}
              className={`rounded-full p-2 transition-colors hover:bg-gray-100 ${
                activeDropdown === 'add' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Plus className="h-6 w-6" />
            </button>
            {activeDropdown === 'add' && (
              <div className="absolute left-0 top-full z-50 mt-2 w-72 rounded-2xl bg-white p-2 text-left shadow-xl ring-1 ring-gray-200">
                <button className="flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-gray-50">
                  <Link className="mt-0.5 h-5 w-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">URL</p>
                    <p className="text-sm text-gray-500">
                      Paste a URL to extract content and images.
                    </p>
                  </div>
                </button>
                <button className="flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-gray-50">
                  <Upload className="mt-0.5 h-5 w-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">Files</p>
                    <p className="text-sm text-gray-500">Upload your image, video or docs.</p>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Ratio Button */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('ratio')}
              className={`flex items-center gap-2 rounded-lg py-2 pl-2 pr-3 text-sm font-medium transition-colors hover:bg-gray-50 ${
                activeDropdown === 'ratio' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <RectangleHorizontal className="h-5 w-5" />
              <span>16:9</span>
            </button>
            {activeDropdown === 'ratio' && (
              <div className="absolute left-0 top-full z-50 mt-2 w-48 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-gray-200">
                <button className="flex w-full items-center justify-between rounded-xl p-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-gray-500" />
                    <span className="font-medium text-gray-700">9:16</span>
                  </div>
                </button>
                <button className="flex w-full items-center justify-between rounded-xl bg-gray-50 p-3">
                  <div className="flex items-center gap-3">
                    <RectangleHorizontal className="h-5 w-5 text-gray-900" />
                    <span className="font-medium text-gray-900">16:9</span>
                  </div>
                  <Check className="h-4 w-4 text-gray-900" />
                </button>
              </div>
            )}
          </div>

          {/* More Button */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('more')}
              className={`flex items-center gap-2 rounded-lg py-2 pl-2 pr-3 text-sm font-medium transition-colors hover:bg-gray-50 ${
                activeDropdown === 'more' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
            {activeDropdown === 'more' && (
              <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-gray-200">
                {[
                  { icon: Clock, label: 'Duration' },
                  { icon: Film, label: 'Assets source' },
                  { icon: Wand2, label: 'AI style' },
                  { icon: Mic, label: 'Voice' },
                ].map((item) => (
                  <button
                    key={item.label}
                    className="flex w-full items-center justify-between rounded-xl p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5 text-gray-500" />
                      <span className="font-medium text-gray-700">{item.label}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleGenerate}
          className="flex cursor-pointer items-center gap-2 rounded-full bg-black px-8 py-3 font-semibold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl"
        >
          <Play className="h-4 w-4 fill-current" />
          <span>Generate</span>
        </button>
      </div>
    </div>
  )
}